import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit, Trash2, Save, X, Search, Check, User } from 'lucide-react';

interface Standort {
  id: string;
  name: string;
  adresse: string;
  strasse: string;
  plz: string;
  stadt: string;
  kunde_id: string;
  standortnummer: string | null;
}

interface Kunde {
  id: string;
  name: string;
  kundennummer: string | null;
}

function StandortVerwaltung() {
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  
  const [formData, setFormData] = useState<Omit<Standort, 'id' | 'adresse'>>({
    name: '',
    strasse: '',
    plz: '',
    stadt: '',
    kunde_id: '',
    standortnummer: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [standorteRes, kundenRes] = await Promise.all([
        supabase.from('standorte').select('*').order('name'),
        supabase.from('kunden').select('id, name, kundennummer').order('name')
      ]);

      if (standorteRes.error) throw standorteRes.error;
      if (kundenRes.error) throw kundenRes.error;
      
      setStandorte(standorteRes.data || []);
      setKunden(kundenRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectCustomer = (kundeId: string) => {
    setFormData(prev => ({
      ...prev,
      kunde_id: kundeId
    }));
    setShowCustomerPopup(false);
    setSearchTerm('');
  };

  const closeCustomerPopup = () => {
    setShowCustomerPopup(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Combine address fields for the adresse field
      const adresse = `${formData.strasse}, ${formData.plz} ${formData.stadt}`.trim();
      
      if (editingId) {
        // Update existing location
        const { error } = await supabase
          .from('standorte')
          .update({
            ...formData,
            adresse
          })
          .eq('id', editingId);

        if (error) throw error;
        
        setStandorte(prev => 
          prev.map(standort => 
            standort.id === editingId ? { 
              ...standort, 
              ...formData,
              adresse
            } : standort
          )
        );
        setEditingId(null);
      } else {
        // Create new location
        const { data, error } = await supabase
          .from('standorte')
          .insert([{
            ...formData,
            adresse
          }])
          .select()
          .single();

        if (error) throw error;
        setStandorte(prev => [...prev, data]);
      }
      
      // Reset form
      setFormData({
        name: '',
        strasse: '',
        plz: '',
        stadt: '',
        kunde_id: '',
        standortnummer: '',
      });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Standorts');
    }
  };

  const startEditing = (standort: Standort) => {
    setEditingId(standort.id);
    
    // Extract address components if they're not already set
    let strasse = standort.strasse || '';
    let plz = standort.plz || '';
    let stadt = standort.stadt || '';
    
    // If the new fields are empty but we have an address, try to parse it
    if ((!strasse || !plz || !stadt) && standort.adresse) {
      const addressParts = standort.adresse.split(',');
      if (addressParts.length > 1) {
        // Format: "Street, Postal City"
        strasse = addressParts[0].trim();
        const cityPart = addressParts[1].trim();
        
        // Try to extract postal code (assuming it's 5 digits)
        const postalMatch = cityPart.match(/\b\d{5}\b/);
        if (postalMatch) {
          plz = postalMatch[0];
          stadt = cityPart.replace(plz, '').trim();
        } else {
          stadt = cityPart;
        }
      } else {
        // No comma, just use the whole address as street
        strasse = standort.adresse;
      }
    }
    
    setFormData({
      name: standort.name,
      strasse,
      plz,
      stadt,
      kunde_id: standort.kunde_id,
      standortnummer: standort.standortnummer || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({
      name: '',
      strasse: '',
      plz: '',
      stadt: '',
      kunde_id: '',
      standortnummer: '',
    });
  };

  const deleteStandort = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Standort löschen möchten?')) {
      return;
    }
    
    try {
      // Check if location has orders
      const { data: auftraege, error: auftraegeError } = await supabase
        .from('auftraege')
        .select('id')
        .eq('standort_id', id);
        
      if (auftraegeError) throw auftraegeError;
      
      if (auftraege && auftraege.length > 0) {
        alert('Dieser Standort hat zugeordnete Aufträge und kann nicht gelöscht werden.');
        return;
      }
      
      const { error } = await supabase
        .from('standorte')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setStandorte(prev => prev.filter(standort => standort.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Standorts');
    }
  };

  const filteredKunden = searchTerm
    ? kunden.filter(kunde => 
        kunde.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (kunde.kundennummer && kunde.kundennummer.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : kunden;

  const selectedKunde = kunden.find(k => k.id === formData.kunde_id);

  // Helper function to format the full address
  const formatAddress = (standort: Standort) => {
    // If we have the new fields, use them
    if (standort.strasse || standort.plz || standort.stadt) {
      const parts = [];
      if (standort.strasse) parts.push(standort.strasse);
      if (standort.plz || standort.stadt) {
        const cityPart = [standort.plz, standort.stadt].filter(Boolean).join(' ');
        if (cityPart) parts.push(cityPart);
      }
      return parts.join(', ');
    }
    
    // Fall back to the original address field
    return standort.adresse;
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Standortverwaltung</h1>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700">Kunde</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCustomerPopup(true)}
                className="mt-1 flex items-center justify-between w-full text-left rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <span className="truncate">
                  {selectedKunde 
                    ? `${selectedKunde.name}${selectedKunde.kundennummer ? ` (${selectedKunde.kundennummer})` : ''}` 
                    : 'Bitte Kunde auswählen...'}
                </span>
                <User size={18} className="text-gray-400" />
              </button>
              
              {showCustomerPopup && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={closeCustomerPopup}
                  ></div>
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
                    <div className="p-2">
                      <div className="mb-2 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search size={16} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          placeholder="Kunde suchen..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      
                      {filteredKunden.length > 0 ? (
                        <div className="max-h-60 overflow-y-auto">
                          {filteredKunden.map((kunde) => (
                            <div 
                              key={kunde.id}
                              className={`flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer ${
                                formData.kunde_id === kunde.id ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => selectCustomer(kunde.id)}
                            >
                              <div className="w-6 h-6 flex items-center justify-center mr-2">
                                {formData.kunde_id === kunde.id && <Check size={16} className="text-blue-600" />}
                              </div>
                              <div>
                                <div className="font-medium">{kunde.name}</div>
                                {kunde.kundennummer && (
                                  <div className="text-sm text-gray-500">#{kunde.kundennummer}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-4 text-center text-gray-500">
                          Keine Kunden gefunden
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {!formData.kunde_id && (
              <p className="mt-1 text-sm text-red-600">Bitte wählen Sie einen Kunden aus</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Standortname</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="z.B. Hauptwerk, Filiale Nord"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Standortnummer</label>
            <input
              type="text"
              name="standortnummer"
              value={formData.standortnummer || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="z.B. S001, WE-123"
            />
            <p className="mt-1 text-xs text-gray-500">
              Kundenspezifische Standortnummer (optional)
            </p>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Straße und Hausnummer</label>
                <input
                  type="text"
                  name="strasse"
                  value={formData.strasse}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. Hauptstraße 123"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Postleitzahl</label>
                <input
                  type="text"
                  name="plz"
                  value={formData.plz}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. 12345"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Stadt</label>
                <input
                  type="text"
                  name="stadt"
                  value={formData.stadt}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. Berlin"
                />
              </div>
            </div>
          </div>
          
          <div className="md:col-span-2 flex justify-end space-x-2">
            {editingId && (
              <button
                type="button"
                onClick={cancelEditing}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
              >
                <X size={20} />
                Abbrechen
              </button>
            )}
            <button
              type="submit"
              disabled={!formData.kunde_id}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {editingId ? (
                <>
                  <Save size={20} />
                  Aktualisieren
                </>
              ) : (
                <>
                  <Plus size={20} />
                  Standort hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standort</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standortnummer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {standorte.map((standort) => {
                const kunde = kunden.find(k => k.id === standort.kunde_id);
                return (
                  <tr key={standort.id} className={editingId === standort.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {kunde ? kunde.name : 'Unbekannter Kunde'}
                      {kunde?.kundennummer && ` (${kunde.kundennummer})`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{standort.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{standort.standortnummer || '-'}</td>
                    <td className="px-6 py-4">{formatAddress(standort)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditing(standort)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Standort bearbeiten"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => deleteStandort(standort.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Standort löschen"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {standorte.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Noch keine Standorte vorhanden
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StandortVerwaltung;